<script setup lang="ts">
import { CalendarCell, CalendarCellTrigger } from 'reka-ui';
import type { DateValue } from 'reka-ui';
import type { EventDateInfo } from '../lib/const';

const props = defineProps<{
  day: DateValue;
  month: DateValue;
  getEventInfo: (day: DateValue) => EventDateInfo | null;
  getCellClasses: (info: EventDateInfo | null) => unknown[];
}>();

const emit = defineEmits<{
  dateClick: [day: DateValue];
}>();

const info = computed(() => props.getEventInfo(props.day));
</script>

<template>
  <CalendarCell :date="day" :class="[getCellClasses(info), $style.cell]">
    <CalendarCellTrigger
      :day="day"
      :month="month"
      :class="$style.trigger"
      @click="emit('dateClick', day)"
    />
    <span v-if="info" :class="$style.tooltip">
      {{ info.name }}
    </span>
  </CalendarCell>
</template>

<style module lang="scss">
.trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-golos);
  font-weight: 500;
  font-size: 18px;
  line-height: 1.1;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: var(--color-gray-line);
  padding: 0;

  &[data-outside-view] {
    color: var(--color-gray-text);
  }

  &[data-disabled] {
    cursor: default;
    opacity: 0.3;
  }

  &:hover:not([data-disabled]) {
    background-color: rgba(255, 255, 255, 0.05);
  }
}

.tooltip {
  display: none;
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--color-black-2);
  backdrop-filter: blur(5px);
  border: 1px solid var(--color-gray-text);
  border-radius: 8px;
  color: var(--color-gray-bg);
  font-family: var(--font-golos);
  font-weight: 500;
  font-size: 11px;
  line-height: 1.1;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  padding: 4px 10px;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
}

.cell:hover .tooltip {
  display: block;
}
</style>
